import {
  ConnectionRoleType,
  OfferingType,
  ItemType,
  ListingFormState,
} from "../types";

type RoleTypeLabels = Record<ConnectionRoleType, string>;

// Depending on the type of offering, different strings may be used
export const memberLabelsOnOfferings: Record<ItemType, RoleTypeLabels> = {
  event: {
    creator: "organizer",
    collaborator: "presenter",
    participant: "attendee",
    listed: "listed",
  },
  product: {
    creator: "creator",
    collaborator: "contributor",
    participant: "user",
    listed: "listed",
  },
  project: {
    creator: "organizer",
    collaborator: "team member",
    participant: "participant",
    listed: "listed",
  },
  service: {
    creator: "provider",
    collaborator: "assistant",
    participant: "client",
    listed: "listed",
  },
  creative: {
    creator: "creator",
    collaborator: "contributor",
    participant: "audience",
    listed: "listed",
  },
  person: {
    creator: "creator", // If a member 'creates' an organization member, they are the creator
    collaborator: "collaborator", // If a member 'collaborates' with another member, they are a collaborator
    participant: "person", // If a member 'participates' in an organization member, they are a member
    listed: "listed",
  },
  organization: {
    creator: "admin",
    collaborator: "moderator",
    participant: "person",
    listed: "listed",
  },
  article: {
    creator: "author",
    collaborator: "contributor",
    participant: "reader",
    listed: "listed",
  },
  course: {
    creator: "instructor",
    collaborator: "teaching assistant",
    participant: "student",
    listed: "listed",
  },
};

export const offeringLabelsOnMembers: Record<ItemType, RoleTypeLabels> = {
  event: {
    creator: "organizer",
    collaborator: "presenter",
    participant: "attendee",
    listed: "listed",
  },
  product: {
    creator: "creator of",
    collaborator: "contributor to",
    participant: "user of",
    listed: "listed",
  },
  project: {
    creator: "organizer",
    collaborator: "team member",
    participant: "participant",
    listed: "listed",
  },
  service: {
    creator: "provider",
    collaborator: "assistant",
    participant: "client",
    listed: "listed",
  },
  creative: {
    creator: "creator of",
    collaborator: "contributor to",
    participant: "audience of",
    listed: "listed",
  },
  person: {
    creator: "creator of", // If a member 'creates' an organization member, they are the creator
    collaborator: "collaborator", // If a member 'collaborates' with another member, they are a collaborator
    participant: "person", // If a member 'participates' in an organization member, they are a member
    listed: "listed",
  },
  organization: {
    creator: "admin of",
    collaborator: "moderator of",
    participant: "member of",
    listed: "listed",
  },
  article: {
    creator: "author of",
    collaborator: "contributor to",
    participant: "reader of",
    listed: "listed",
  },
  course: {
    creator: "instructor of",
    collaborator: "teaching assistant for",
    participant: "student in",
    listed: "listed",
  },
};

// Depending on the type of offering, different strings may be used
export const defaultTypeOptions: Record<ItemType, Record<string, string>> = {
  event: {
    workshop: "Workshop",
    webinar: "Webinar",
    meetup: "Meetup",
    conference: "Conference",
    retreat: "Retreat",
    festival: "Festival",
    other: "Other",
  },
  product: {
    art: "Art",
    craft: "Craft",
    digital: "Digital",
    food: "Food",
    furniture: "Furniture",
    clothing: "Clothing",
    other: "Other",
  },
  project: {
    research: "Research",
    community: "Community",
    environmental: "Environmental",
    technology: "Technology",
    art: "Art",
    other: "Other",
  },
  service: {
    consulting: "Consulting",
    coaching: "Coaching",
    mentoring: "Mentoring",
    freelance: "Freelance",
    tutoring: "Tutoring",
    other: "Other",
  },
  creative: {
    writing: "Writing",
    music: "Music",
    visual_art: "Visual Art",
    performance: "Performance",
    film: "Film",
    design: "Design",
    other: "Other",
  },
  person: {
    app: "App",
    researcher: "Researcher",
    artist: "Artist",
    organization: "Organization",
    dream_group: "Dream Group",
    individual: "Individual",
    collective: "Collective",
    other: "Other",
  },
  organization: {
    community: "Community",
    directory: "Directory",
    collective: "Collective",
    other: "Other",
  },
  course: {
    online: "Online Course",
    in_person: "In-Person Course",
    hybrid: "Hybrid Course",
    other: "Other",
  },
  article: {
    blog_post: "Blog Post",
    research_paper: "Research Paper",
    news_article: "News Article",
    opinion_piece: "Opinion Piece",
    other: "Other",
  },
};

// Labels for the keys of Offering (e.g. startDate -> Available From)
type OfferingFieldLabels = {
  [K in keyof ListingFormState]?: string;
};

// These differ depending on the type of offering.
// Keys are:
// id: string;
// dateAdded: string;
// name: string;
// description: string;
// approvalStatus: ApprovalStatus;
// url: string;
// imageUrl: string;
// subType?: string;
// type: OfferingType;
// startDate?: string;
// endDate?: string;
// approximateDate
// dateLabel?: string;
export const formFieldLabels: Record<OfferingType, OfferingFieldLabels> = {
  event: {
    name: "Event Name",
    description: "Event Description",
    url: "Event URL",
    subType: "Event Type",
    startDate: "Event Start Date",
    endDate: "Event End Date",
    dateLabel: "Describe the time(s) your event will occur",
    isOnline: "Online Event",
  },
  product: {
    name: "Product Name",
    description: "Product Description",
    url: "Product URL",
    subType: "Product Type",
    startDate: "Available From",
    endDate: "Available Until",
    dateLabel: "Describe when your product is available",
    isOnline: "Virtual Product",
  },
  project: {
    name: "Project Name",
    description: "Project Description",
    url: "Project URL",
    subType: "Project Type",
    startDate: "Project Start Date",
    endDate: "Project End Date",
    dateLabel: "Describe the time frame of your project",
    isOnline: "Online Project",
  },
  service: {
    name: "Service Name",
    description: "Service Description",
    url: "Service URL",
    subType: "Service Type",
    startDate: "Available From",
    endDate: "Available Until",
    dateLabel: "Describe when your service is available",
    isOnline: "Online Service",
  },
  creative: {
    name: "Creative Work Name",
    description: "Creative Work Description",
    url: "Creative Work URL",
    subType: "Creative Work Type",
    startDate: "Available From",
    endDate: "Available Until",
    dateLabel: "Describe when your creative work is available",
    isOnline: "Virtual Creative Work",
  },
  article: {
    name: "Article Title",
    description: "Article Description",
    url: "Article URL",
    subType: "Article Type",
    startDate: "Published On",
    dateLabel: "Describe when your article was published",
  },
  course: {
    name: "Course Name",
    description: "Course Description",
    url: "Course URL",
    subType: "Course Type",
    startDate: "Course Start Date",
    endDate: "Course End Date",
    dateLabel: "Describe the time frame of your course",
    isOnline: "Online Course",
  },
};

export const getRoleLabel = (
  offeringType: ItemType | undefined,
  role: ConnectionRoleType
): string => {
  if (!offeringType) {
    return "";
  }
  return memberLabelsOnOfferings[offeringType][role] || "Member";
};
